import type { LinkGrantFactorKind, ScanConfigResponse, SubmitScanRequest } from '@/api/types'
import type { TranslationKey } from '@/i18n/translations'

/**
 * The whole patient journey as one discriminated union rather than several booleans, so an
 * impossible combination (mid-scan with no access token, say) cannot be represented and does not
 * need a test proving it cannot happen.
 */
export type FlowState =
  | { readonly step: 'challenging' }
  | { readonly step: 'date-of-birth'; readonly factor: LinkGrantFactorKind }
  | { readonly step: 'dead-link' }
  | { readonly step: 'rate-limited' }
  | { readonly step: 'loading-config'; readonly accessToken: string }
  | { readonly step: 'scan-unavailable' }
  | { readonly step: 'ready'; readonly accessToken: string; readonly config: ScanConfigResponse }
  /**
   * The tap has happened and the browser has not yet answered for the camera - which is exactly as
   * long as its permission dialog is up (scan-flow.tsx `onStart`).
   */
  | {
      readonly step: 'opening-camera'
      readonly accessToken: string
      readonly config: ScanConfigResponse
    }
  | {
      readonly step: 'scanning'
      readonly accessToken: string
      readonly config: ScanConfigResponse
    }
  | {
      readonly step: 'complete'
      /**
       * What was actually measured, for the patient to read - and only ever the fields the vendor
       * produced, because the submit shape omits what it did not (to-submit-request.ts).
       *
       * Absent on a hybrid scan whose reading was submitted thirty seconds in (docs/decisions.md
       * D-42), where this state is reached without the request in hand.
       */
      readonly results?: SubmitScanRequest
    }
  /**
   * A scan that produced nothing, twice, after the patient had already been offered one retry.
   *
   * Its own step rather than `complete` with a flag, because the two screens make DIFFERENT promises:
   * one says a measurement was taken and sent, and this one must not - there is nothing to send. What
   * it does not do is mention the failure, which is the whole point of it.
   */
  | { readonly step: 'thanked' }
  | {
      readonly step: 'scan-failed'
      // A key, not a sentence. The screen translates it, so nothing a patient reads is decided by
      // whichever component happened to construct this state - and an exception's own message can no
      // longer arrive here by being assigned to a string.
      readonly messageKey: TranslationKey
      readonly accessToken: string
      readonly config: ScanConfigResponse
    }

/** Auth's own documented cap (architecture/services/platform-auth.md §5: "the max-attempts cap").
 * A public, architectural fact — not something read off any one response — see D-8. */
export const MAX_DATE_OF_BIRTH_ATTEMPTS = 3
